import { createI18n } from 'vue-i18n';

import { env } from '@/constants/env';
import { normalizeLocale } from '@/utils/i18n';
import { mapTestKeys } from '@test/utils/helpers';

import enUS from '../../locales/en-US.json';
import es from '../../locales/es.json';

const DEFAULT_LOCALE = 'en-US'; // TODO: Rename en-US to en
const locale = normalizeLocale(import.meta.env.VITE_I18N_LOCALE || DEFAULT_LOCALE);
const lang = locale.split('-')[0];
// Used for loading fallback locale JSON, and defining Vue-i18n fallbacks
// We can later expand on this for special cases if need be.
const fallbackLocales = [lang, DEFAULT_LOCALE];
const locales = new Set([locale, ...fallbackLocales]);
const messages = {};

// Load global messages for the locale chain (eg. es-PR? es? en-US)
for (const locale of locales) {
  /**
   * HACK: this is avoiding dynamic imports because webpack and vite do not handle them the same.
   * Until we get rid of webpack or have two different solutions, I'm hardcoding this to make them
   * both happy.  Ideally, we use i18n config to just load up the locales like in this first example
   * https://github.com/intlify/vite-plugin-vue-i18n.  We're not saving anything by dynamically
   * importing them because the json files are still bundled
   **/
  if (locale === 'en-US') {
    messages['en-US'] = enUS;
  } else if (locale === 'es') {
    messages['es'] = es;
  }

  /**
   * For testing translation coverage - wraps all messages with ⸨  ⸩
   * Uses TEST_I18N env flag, and Django constance
   */
  if (messages[locale] && import.meta.env.VITE_TEST_I18N) {
    messages[locale] = mapTestKeys(messages[locale]);
  }
}

// `choiceOptions` relates to the number of options for the specified translation
// `i18n.tc()` accepts 2 or 3 options, so the code is covering both cases
// In case of translation having only 2 options, empty and single options will be the same
// `choiceOptions` value is the length of options for that translation
const getOptionsByTranslationChoices = (choiceOptions) =>
  choiceOptions === 3
    ? {
        returnAsEmpty: 0,
        returnAsSingle: 1,
        returnAsPlural: 2,
      }
    : {
        returnAsEmpty: 0,
        returnAsSingle: 0,
        returnAsPlural: 1,
      };

/**
 * A helper function for applying decimals as plural for english speakers
 * @param {number} [choice] - Number passed to `i18n.tc()` function as value to be resolved
 * @param {number} [choiceOptions] - `3` or `2`: Number of options available on the translation file to that specific key
 * @returns {number} 0: `empty`, 1: `single`, 2: `plural`
 */
const applyingDecimalsAsPluralForEnglishSpeakers = (choice, choiceOptions) => {
  // These codes are related to `vue-i18n` internals to pluralize resolution
  //  - 0: it should return empty value as result
  //  - 1: it should return single value as result
  //  - 2: it should return plural value as result
  const { returnAsEmpty, returnAsSingle, returnAsPlural } = getOptionsByTranslationChoices(choiceOptions);

  // Returns empty if receives `0` as value
  if (choice === 0) {
    return returnAsEmpty;
  }

  // NOTE: In english, decimals are also plural
  // This function always receives value as `number`, so it's safe to assume
  // decimal values as result of `!Number.isInteger()` and return as plural
  if (!Number.isInteger(choice)) {
    return returnAsPlural;
  }

  // Otherwise, check if number is more than 1 to return as plural or single
  return choice > 1 ? returnAsPlural : returnAsSingle;
};

const pluralizationRules = {
  'en-US': applyingDecimalsAsPluralForEnglishSpeakers,
  en: applyingDecimalsAsPluralForEnglishSpeakers,
};

const _i18n = createI18n({
  locale,
  fallbackLocale: fallbackLocales,
  messages,
  silentTranslationWarn: true,
  silentFallbackWarn: true,
  legacy: true, // in combination with `compositionOnly: false` in vite.config.ts
  pluralizationRules,
  ...(env.MODE === 'test'
    ? {
        missingWarn: false,
        fallbackWarn: false,
        // Since we have `legacy: true`, this config should receive `off` instead of `false`
        // More details in https://stackoverflow.com/questions/69795845/vue-3-eslint-warning-intlify-detected-html#answer-73513649
        // Reference: https://kazupon.github.io/vue-i18n/api/#warnhtmlinmessage-2
        warnHtmlInMessage: 'off',
      }
    : {}),
});

// Applying proper types to cover our migration scenario from `legacy` Vue components to `composition` usage.
// Extracted from `i18n.global` types
//
//    If the I18n#mode is 'legacy', then you can access to a global VueI18n instance,
//    else then I18n#mode is 'composition' , you can access to the global Composer instance.
type I18n = Omit<typeof _i18n, 't' | 'tc' | 'te'> & Pick<typeof _i18n.global, 't' | 'tc' | 'te'>;

const i18n: I18n = {
  ..._i18n,
  t: _i18n.global.t,
  tc: _i18n.global.tc,
  te: _i18n.global.te,
};

/**
 * Mapping all the files loaded in async to make sure we're tracking of that usage
 * So that, we can also enforce these files to be loaded in our test setup
 */
export type AllowedTranslationVerticals =
  | 'inventory'
  | 'admin'
  | 'advertising'
  | 'shop'
  | 'cart'
  | 'company'
  | 'crm'
  | 'enrollment'
  | 'logistics'
  | 'orders'
  | 'pricing'
  | 'replenishment'
  | 'reports'
  | 'seedToSale'
  | 'settings'
  | 'shipAssist'
  | 'warehouse'
  | 'user'
  | 'advertising'
  | 'productRecommendation'
  | 'recentlyPurchased';

/**
 * Loads a vertical's translations on-demand. Resolves if already loaded.
 * If the English translations were already loaded for a vertical as part
 * of a different language, it won't reload those.
 * @param {string} vertical The vertical to load
 * @returns {Promise}
 */
async function loadTranslations(vertical: AllowedTranslationVerticals) {
  const isLocaleVerticalLoaded = _i18n.global.getLocaleMessage(locale)[vertical];
  const isDefaultVerticalLoaded = _i18n.global.getLocaleMessage(DEFAULT_LOCALE)[vertical];

  // If the vertical translations were already loaded for the given locale
  if (isLocaleVerticalLoaded) {
    return;
  }

  // Avoid re-loading the English vertical translations if already loaded
  if (isDefaultVerticalLoaded) {
    locales.delete(DEFAULT_LOCALE);
  }

  // Load all vertical translations for each applicable locale in parallel.
  // TODO: Eventually replace with Promise.allSettled
  await Promise.all(Array.from(locales).map((locale) => loadVertical(locale, vertical)));
}

const loadVertical = async (locale, vertical) => {
  let data;

  try {
    data = await import(`../../locales/${locale}/${vertical}.json`);
  } catch (e) {
    // Do nothing. JSON does not exist for given locale in locales chain
    return;
  }

  let messages = data.default;

  /**
   * For testing translation coverage - wraps all messages with ⸨  ⸩
   * Uses TEST_I18N env flag, and Django constance
   * */
  if (import.meta.env.VITE_TEST_I18N) {
    messages = mapTestKeys(messages);
  }

  // Merge vertical messages that were just loaded with existing locale messages
  const localeMessages = {
    ..._i18n.global.getLocaleMessage(locale),
    [vertical]: messages,
  };

  // Merge vertical messages into existing vue-i18n messages
  _i18n.global.setLocaleMessage(locale, localeMessages);
};

export { loadTranslations, locale };
export default i18n;
